// ============================================================
// competicoes.js — a leitura de um resultado lançado, do lado do painel
//
// A CONTAGEM não mora aqui. Participações, pódios, medalhas e
// aproveitamento saem de `resumoDoAtleta`, em functions/resultados.js, e
// chegam prontos na resposta de `rankingDeMedalhas`. Número derivado tem um
// dono só: duas contas do mesmo fato divergem no dia em que alguém corrige
// uma delas, e a planilha de 2026 é a demonstração disso — a linha "TOTAL
// DE MEDALHAS" era digitada à mão em três abas e as três discordavam.
//
// O que mora aqui é a LEITURA de uma colocação para a tela: o que escrever
// ao lado da prova, que medalha é aquela, e como juntar as categorias de um
// mesmo evento numa linha do tempo. É apresentação, e precisa existir no
// cliente porque a resposta do servidor traz o que foi DIGITADO no campo de
// colocação, não o rótulo que se lê.
//
// As três regras do domínio, as mesmas do servidor:
//
//   1. Pódio é por modalidade. No BMX premia-se até o 5º, no skate e no
//      tênis de mesa até o 3º. `tamanhoDoPodio` vem do cadastro do núcleo.
//   2. Fase não é colocação. Em chave de 16, quem perde nas oitavas pode
//      terminar 9º ou 16º, e o resultado oficial não diz qual. Fase conta
//      como participação e nunca como pódio.
//   3. Quem não competiu não tem colocação nem aproveitamento. "NC" é
//      diferente de 0%: zero por cento diz que o atleta competiu e não
//      pontuou.
// ============================================================

// Situações que não são colocação. `disputou` é o que separa quem esteve na
// prova de quem não esteve.
export const SITUACOES = {
  // NC CONTA como participação: o atleta esteve na prova e não passou.
  NC: { rotulo: "Não classificou", disputou: true },
  // ND não conta: ele não esteve.
  ND: { rotulo: "Não disputou", disputou: false },

  // Códigos antigos, aceitos e nunca oferecidos.
  //
  // Eram "desclassificado" e "lesionou na prova", e os dois viraram NC por
  // decisão da coordenação: os três diziam a mesma coisa para o quadro,
  // que é "esteve na prova e não tem colocação". Ficam aqui para que
  // nenhum lançamento antigo, de importação ou de outro núcleo, vire
  // colocação inválida e trave a gravação da competição inteira.
  DC: { rotulo: "Não classificou", disputou: true, oculto: true },
  LE: { rotulo: "Não classificou", disputou: true, oculto: true },
};

// Fase alcançada, da mais longe à mais perto do título. O peso serve só
// para comparar duas fases entre si quando se ordena a lista de provas de
// um evento; não é posição e não vira posição.
export const FASES = {
  "fase de grupos": 0,
  "fase de grupo": 0,
  "nao classificou": 0,
  "classificatorias": 0,
  "fase 256": 1,
  "fase 128": 2,
  "fase 64": 3,
  "fase 32": 4,
  "fase 16": 5,
  "oitavas de final": 6,
  "quartas de final": 7,
  "semifinal": 8,
};

// Os combinantes de acento escritos por código, e não como literal: um
// combinante solto dentro de uma classe de caracteres desaparece em
// qualquer salvamento que normalize o texto do arquivo.
const DIACRITICOS = new RegExp(
  "[" + String.fromCharCode(0x300) + "-" + String.fromCharCode(0x36f) + "]",
  "g"
);

function chaveDaFase(v) {
  return String(v || "")
    .normalize("NFD")
    .replace(DIACRITICOS, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

// Interpreta o que foi digitado no campo de colocação.
//
// Devolve sempre a mesma forma, para a tela não precisar saber de qual dos
// três tipos de resultado se trata antes de perguntar. `posicao` é número
// só quando é número: "3º", "3" e " 3 " chegam iguais, porque ninguém
// digita de um jeito só.
export function lerColocacao(bruto) {
  const texto = String(bruto === undefined || bruto === null ? "" : bruto).trim();
  const vazio = { disputou: false, posicao: null, situacao: null, fase: null, pesoDaFase: null };
  if (!texto) return vazio;

  const codigo = texto.toUpperCase();
  if (Object.prototype.hasOwnProperty.call(SITUACOES, codigo)) {
    return {
      disputou: SITUACOES[codigo].disputou,
      posicao: null, situacao: codigo, fase: null, pesoDaFase: null,
    };
  }

  const chave = chaveDaFase(texto);
  if (Object.prototype.hasOwnProperty.call(FASES, chave)) {
    // O texto volta como foi digitado, e não normalizado: a tela mostra
    // "Oitavas de Final" como está na folha de resultados.
    return { disputou: true, posicao: null, situacao: null, fase: texto, pesoDaFase: FASES[chave] };
  }

  const n = Number(texto.replace(/[ºo°.\s]/gi, ""));
  if (Number.isFinite(n) && n >= 1) {
    return { disputou: true, posicao: Math.round(n), situacao: null, fase: null, pesoDaFase: null };
  }
  return vazio;
}

// O que se escreve ao lado da prova.
//
// Traço para campo vazio, que é "ainda não lancei", e não "não competiu" —
// a diferença é a mesma que o lançamento faz, e a ficha não pode apagá-la.
export function rotuloDaColocacao(bruto) {
  const c = lerColocacao(bruto);
  if (c.posicao !== null) return `${c.posicao}º`;
  if (c.fase) return c.fase;
  if (c.situacao) return SITUACOES[c.situacao].rotulo;
  return "—";
}

// A medalha de uma colocação, dado o tamanho do pódio da modalidade.
//
// Pódio e medalha são a mesma coisa: quem sobe, recebe. No BMX os cinco
// recebem, do mesmo jeito que os três nas outras modalidades — por isso do
// quarto lugar em diante o rótulo é "Medalha" e não "Bronze".
export function medalhaDe(valor, tamanhoDoPodio) {
  const { posicao } = lerColocacao(valor);
  if (posicao === null) return null;
  if (posicao > (tamanhoDoPodio || 3)) return null;
  if (posicao === 1) return { metal: "ouro", sinal: "🥇", txt: "Ouro" };
  if (posicao === 2) return { metal: "prata", sinal: "🥈", txt: "Prata" };
  if (posicao === 3) return { metal: "bronze", sinal: "🥉", txt: "Bronze" };
  return { metal: "outras", sinal: "🏅", txt: "Medalha" };
}

// Ordem de leitura das provas dentro de um evento: o melhor resultado
// primeiro.
//
// Colocação vem antes de fase porque é mais específica, e fase vem antes de
// situação porque o atleta esteve na prova. Entre duas fases decide o peso;
// entre duas colocações, o número. Empate resolve pelo nome da categoria,
// para a ordem não variar entre duas aberturas da mesma ficha.
function postoDaProva(p) {
  const c = lerColocacao(p && p.colocacao);
  if (c.posicao !== null) return [0, c.posicao];
  if (c.fase) return [1, -c.pesoDaFase];
  if (c.situacao) return [2, c.disputou ? 0 : 1];
  return [3, 0];
}

function compararProvas(a, b) {
  const [fa, va] = postoDaProva(a);
  const [fb, vb] = postoDaProva(b);
  if (fa !== fb) return fa - fb;
  if (va !== vb) return va - vb;
  return String(a.categoria || "").localeCompare(String(b.categoria || ""), "pt-BR");
}

// A linha do tempo do atleta: um bloco por EVENTO, com as categorias
// agrupadas dentro.
//
// Agrupado porque uma linha de lançamento é uma categoria, não um evento. A
// Alissa disputou cinco categorias na Copa Brasil Ouro de Teresina num fim
// de semana só: listar cinco entradas faria parecer cinco competições, que
// é justamente o erro que o cálculo de aproveitamento evita.
//
// Do mais recente para o mais antigo, porque a pergunta que se faz abrindo
// a ficha é "como ele está agora".
export function agruparPorEvento(lancamentos) {
  const lista = Array.isArray(lancamentos) ? lancamentos : [];
  const eventos = new Map();

  lista.forEach((l) => {
    if (!l) return;
    // Sem id de evento, cada lançamento é o seu próprio evento: é o que
    // acontece com resultado lançado antes de a competição ter documento, e
    // continua certo para quem lança um resultado por prova.
    const id = l.competicaoId || l.eventoId || `${l.competicao || ""}|${l.dataInicio || ""}`;
    if (!eventos.has(id)) {
      eventos.set(id, {
        id,
        nome: l.competicao || "(competição sem nome)",
        data: l.dataInicio || null,
        local: l.local || null,
        provas: [],
      });
    }
    eventos.get(id).provas.push(l);
  });

  return [...eventos.values()]
    .map((e) => {
      const provas = [...e.provas].sort(compararProvas);
      return { ...e, provas, melhor: provas[0] || null };
    })
    .sort((a, b) => {
      const d = String(b.data || "").localeCompare(String(a.data || ""));
      return d !== 0 ? d : a.nome.localeCompare(b.nome, "pt-BR");
    });
}

// O melhor resultado do período, com o evento e a categoria em que
// aconteceu.
//
// `melhorColocacao` do resumo do servidor já diz o NÚMERO. A ficha mostra
// onde ele foi conquistado, que é o que torna o número uma informação: "4º
// lugar" não diz nada; "4º na Elite do Cearense" diz.
//
// Só colocação numérica concorre. Fase não entra porque não é posição, e
// comparar "Semifinal" com "7º lugar" exigiria inventar a posição que a
// semifinal não publicou.
export function melhorDoAno(lancamentos) {
  const lista = (Array.isArray(lancamentos) ? lancamentos : [])
    .filter((l) => l && lerColocacao(l.colocacao).posicao !== null);
  if (!lista.length) return null;

  const ordenada = [...lista].sort((a, b) => {
    const pa = lerColocacao(a.colocacao).posicao;
    const pb = lerColocacao(b.colocacao).posicao;
    if (pa !== pb) return pa - pb;
    // Empate: a categoria principal primeiro, porque é o nível do atleta, e
    // depois a mais recente. Um 1º lugar na principal e um 1º na secundária
    // não são a mesma conquista, e a ficha mostra a do nível dele.
    const ta = a.tipo === "principal" ? 0 : 1;
    const tb = b.tipo === "principal" ? 0 : 1;
    if (ta !== tb) return ta - tb;
    return String(b.dataInicio || "").localeCompare(String(a.dataInicio || ""));
  });

  const m = ordenada[0];
  return {
    posicao: lerColocacao(m.colocacao).posicao,
    categoria: m.categoria || null,
    tipo: m.tipo || null,
    competicao: m.competicao || null,
    data: m.dataInicio || null,
  };
}

// Quantos eventos distintos, para a tela não recontar de um jeito diferente
// do servidor. O número oficial continua sendo o `competicoes` do resumo;
// este serve para a ficha de quem ainda não tem resumo carregado.
export function contarEventos(lancamentos) {
  return agruparPorEvento(lancamentos).length;
}

// ── Quem disputa mais de uma categoria no mesmo evento ──────
//
// No tênis de mesa, sim: um Cearense rende Sub 13, Absoluto e Duplas ao
// mesmo atleta, e por isso provas e competições são números diferentes
// e o aproveitamento divide por provas. No BMX e no skate o atleta corre
// a categoria dele e só ela, então os dois números são sempre iguais.
//
// Isto existe porque a legenda do relatório explicava as três
// modalidades de uma vez: a ficha de um piloto de BMX falava de disputar
// várias categorias no mesmo evento, que é coisa que nunca acontece com
// ele, e quem lê ficava procurando no próprio quadro o que não estava
// lá.
export const VARIAS_CATEGORIAS_POR_EVENTO = ["Tênis de Mesa"];

export function disputaVariasCategorias(modalidade) {
  return VARIAS_CATEGORIAS_POR_EVENTO.includes(modalidade);
}

// A legenda do quadro de medalhas de UMA modalidade.
//
// Devolve uma lista de frases, porque o PDF imprime uma por linha e a
// tela, quando mostrar, vai querer o mesmo corte.
export function legendaDoQuadro({ modalidade, tamanhoDoPodio }) {
  const ate = Math.max(1, Number(tamanhoDoPodio) || 3);
  const varias = disputaVariasCategorias(modalidade);
  return [
    `Pódio desta modalidade: até o ${ate}º colocado.`,
    "Pódios é quantas vezes o atleta subiu ao pódio.",
    varias
      ? "Provas são categorias disputadas, e competições são eventos: em "
        + `${modalidade} o mesmo atleta pode disputar mais de uma categoria no mesmo evento.`
      : `Em ${modalidade} o atleta disputa a categoria dele em cada competição, então provas `
        + "e competições andam juntas.",
    varias
      ? "Aproveitamento é pódios sobre provas."
      : "Aproveitamento é pódios sobre provas disputadas.",
  ];
}
